import styled from "@emotion/styled";
import { useEffect, useState } from "react";

import { PageSection } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { cancelSubscription, navigateToCheckout, updateSubscription } from "#actions";

import { plansMeta } from "../planMetadata.ts";
import { useCurrentPlanName, useInteractProduct, useRefreshAccess } from "../useAccount.ts";
import { PlanCard, type PlanState } from "./PlanCard.tsx";

export const PlansSection = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const refreshAccount = useRefreshAccess();
  const product = useInteractProduct();
  const currentPlanName = useCurrentPlanName();

  const [state, setState] = useState<PlanState>("loading");

  useEffect(() => {
    setState(!product ? "loading" : product.subscription?.pendingUpdate ? "updating" : "idle");
  }, [product]);

  const onChoosePlan = async (priceKey: string | null) => {
    if (!product) {
      throw new Error("No product");
    }
    setState("updating");
    try {
      if (product.subscription) {
        if (priceKey) {
          await updateSubscription(product.subscription.id, {
            price: priceKey,
          });
        } else {
          await cancelSubscription(product.subscription.id);
        }
        await refreshAccount();
      } else if (priceKey) {
        await navigateToCheckout(priceKey, {
          returnUrl: "/settings/billing",
          completeUrl: "/settings/billing/complete",
        });
      }
    } finally {
      setState("idle");
    }
  };

  return (
    <PageSection
      header={
        <>
          <h3>{formatMessage("header")}</h3>
          <p>{formatMessage("subHeader", { planName: currentPlanName })}</p>
        </>
      }
    >
      <PlansContainer>
        {plansMeta.map((plan) => (
          <PlanCard key={plan.key} meta={plan} product={product} state={state} onChoosePlan={onChoosePlan} />
        ))}
      </PlansContainer>
    </PageSection>
  );
};

const PlansContainer = styled.div`
  display: flex;
  align-items: stretch;
`;

const formatStrings = {
  header: {
    en: "Plans",
  },
  subHeader: {
    en: "You are currently on the {planName} Plan. Choose the right plan for you below.",
  },
};
